// SwipeDeck Styled Components

import styled, { keyframes } from 'styled-components';
import { theme } from '../theme';

const spin = keyframes`
  0% { transform: rotate(0deg); }
  100% { transform: rotate(360deg); }
`;

const fadeIn = keyframes`
  from { opacity: 0; }
  to { opacity: 1; }
`;

const slideUp = keyframes`
  from { 
    opacity: 0;
    transform: translateY(20px);
  }
  to { 
    opacity: 1;
    transform: translateY(0);
  }
`;

export const SwipeDeckContainer = styled.div`
  display: flex;
  flex-direction: column;
  height: 100%;
  /* min-height: 0 so this can SHRINK inside its flex parent. A hard 600px
   * floor made the deck taller than a short phone's viewport, which pushed the
   * action buttons out of sight — the column cannot shrink below its
   * min-height, so the overflow had to go somewhere and it went off the
   * bottom. The desktop floor is restored below, where there is room for it. */
  min-height: 0;
  background: ${theme.colors.background.primary};
  position: relative;
  padding: ${theme.spacing.lg};
  padding-top: 0;

  @media (min-width: ${theme.breakpoints.tablet}) {
    min-height: 600px;
  }

  @media (max-width: ${theme.breakpoints.mobile}) {
    padding: ${theme.spacing.md};
    padding-top: 0;
    /* Fills what is visible without demanding more than that. */
    height: 100%;
    max-height: 100%;
    overflow: hidden;
  }
`;

export const SwipeDeckHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  flex-shrink: 0;
  margin-bottom: ${theme.spacing.xl};
  animation: ${slideUp} 0.4s ease-out;
  
  @media (max-width: ${theme.breakpoints.mobile}) {
    flex-direction: column;
    gap: ${theme.spacing.md};
    margin-bottom: ${theme.spacing.lg};
  }
`;

export const SwipeDeckTitle = styled.h1`
  font-size: 2rem;
  font-weight: 700;
  color: ${theme.colors.text.primary};
  margin: 0;
  
  @media (max-width: ${theme.breakpoints.mobile}) {
    font-size: 1.5rem;
  }
`;

export const SwipeDeckStats = styled.div`
  display: flex;
  gap: ${theme.spacing.lg};
  
  @media (max-width: ${theme.breakpoints.mobile}) {
    gap: ${theme.spacing.md};
    width: 100%;
    justify-content: space-around;
  }
`;

export const StatItem = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 4px;
`;

export const StatValue = styled.div`
  font-size: 1.5rem;
  font-weight: 700;
  color: ${theme.colors.primary};
  line-height: 1;
  
  @media (max-width: ${theme.breakpoints.mobile}) {
    font-size: 1.25rem;
  }
`;

export const StatLabel = styled.div<{ $isFull?: boolean }>`
  font-size: 0.75rem;
  color: ${props => props.$isFull ? theme.colors.error : theme.colors.text.secondary};
  text-transform: uppercase;
  letter-spacing: 0.5px;
  font-weight: 600;
  
  @media (max-width: ${theme.breakpoints.mobile}) {
    font-size: 0.7rem;
  }
`;

export const SwipeDeckContent = styled.div`
  flex: 1;
  display: flex;
  align-items: center;
  justify-content: center;
  position: relative;
  /* The card area is what gives way when the screen is short: min-height: 0
   * lets this flex item shrink so the header above and the buttons below stay
   * on screen. Without it a flex child refuses to go below its content size. */
  min-height: 0;
  animation: ${fadeIn} 0.6s ease-out;
  width: 100%;

  @media (min-width: ${theme.breakpoints.tablet}) {
    min-height: 500px;
  }

  @media (max-width: ${theme.breakpoints.mobile}) {
    margin-top: 0;
    padding-top: 0;
    align-items: center;
    overflow: hidden;
  }
`;

export const SwipeDeckFooter = styled.div`
  display: flex;
  justify-content: center;
  flex-shrink: 0;
  margin-top: ${theme.spacing.xl};
  animation: ${slideUp} 0.6s ease-out;
  
  @media (max-width: ${theme.breakpoints.mobile}) {
    margin-top: ${theme.spacing.lg};
  }
`;

export const RefreshButton = styled.button`
  background: ${theme.colors.background.secondary};
  color: ${theme.colors.text.primary};
  border: 2px solid ${theme.colors.border};
  border-radius: ${theme.borderRadius.full};
  padding: ${theme.spacing.sm} ${theme.spacing.xl};
  font-size: 1rem;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.2s ease;
  display: flex;
  align-items: center;
  gap: ${theme.spacing.xs};
  
  &:hover:not(:disabled) {
    background: ${theme.colors.primary};
    color: white;
    border-color: ${theme.colors.primary};
    transform: translateY(-2px);
    box-shadow: ${theme.shadows.medium};
  }
  
  &:active:not(:disabled) {
    transform: translateY(0);
  }
  
  &:disabled {
    opacity: 0.5;
    cursor: not-allowed;
  }
  
  @media (max-width: ${theme.breakpoints.mobile}) {
    padding: ${theme.spacing.sm} ${theme.spacing.lg};
    font-size: 0.9rem;
  }
`;

export const EmptyState = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  text-align: center;
  padding: ${theme.spacing.xl};
  animation: ${fadeIn} 0.4s ease-out;
`;

export const EmptyStateIcon = styled.div`
  font-size: 5rem;
  margin-bottom: ${theme.spacing.lg};
  
  @media (max-width: ${theme.breakpoints.mobile}) {
    font-size: 4rem;
    margin-bottom: ${theme.spacing.md};
  }
`;

export const EmptyStateTitle = styled.h2`
  font-size: 1.75rem;
  font-weight: 700;
  color: ${theme.colors.text.primary};
  margin: 0 0 ${theme.spacing.md} 0;
  
  @media (max-width: ${theme.breakpoints.mobile}) {
    font-size: 1.5rem;
  }
`;

export const EmptyStateMessage = styled.p`
  font-size: 1rem;
  color: ${theme.colors.text.secondary};
  margin: 0 0 ${theme.spacing.xl} 0;
  max-width: 400px;
  line-height: 1.5;
  
  @media (max-width: ${theme.breakpoints.mobile}) {
    font-size: 0.9rem;
    margin-bottom: ${theme.spacing.lg};
  }
`;

export const LoadingOverlay = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  height: 100%;
  min-height: 400px;
  animation: ${fadeIn} 0.3s ease-out;
`;

export const LoadingSpinner = styled.div`
  width: 60px;
  height: 60px;
  border: 4px solid ${theme.colors.background.secondary};
  border-top-color: ${theme.colors.primary};
  border-radius: ${theme.borderRadius.full};
  animation: ${spin} 1s linear infinite;
  margin-bottom: ${theme.spacing.lg};
`;

export const LoadingText = styled.p`
  font-size: 1.125rem;
  color: ${theme.colors.text.secondary};
  margin: 0;
  font-weight: 500;
  
  @media (max-width: ${theme.breakpoints.mobile}) {
    font-size: 1rem;
  }
`;

export const MobileInstructionText = styled.div`
  text-align: center;
  color: rgba(255, 255, 255, 0.95);
  font-size: 0.75rem;
  font-weight: 500;
  margin-top: ${theme.spacing.md};
  margin-bottom: 0;
  padding: 0.375rem 0.875rem;
  letter-spacing: 0.02em;
  position: relative;
  z-index: 50;
  width: 100%;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: ${theme.spacing.xs};
  background: transparent;
  backdrop-filter: none;
  border-radius: 12px;
  margin-left: auto;
  margin-right: auto;
  max-width: 90%;
  
  @media (max-width: ${theme.breakpoints.mobile}) {
    margin-top: ${theme.spacing.sm};
    margin-bottom: 0;
    padding: 0.25rem 0.75rem;
  }
  
  @media (max-width: 480px) {
    font-size: 0.6875rem;
    margin-top: ${theme.spacing.sm};
    margin-bottom: 0;
    padding: 0.25rem 0.625rem;
    border-radius: 10px;
    gap: ${theme.spacing.xs};
  }
  
  @media (max-width: 360px) {
    font-size: 0.625rem;
    margin-top: ${theme.spacing.xs};
    margin-bottom: 0;
    padding: 0.1875rem 0.5rem;
    border-radius: 8px;
    gap: 2px;
  }
`;

export const InstructionIcon = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  width: 32px;
  height: 32px;
  border-radius: ${theme.borderRadius.md};
  background: rgba(0, 0, 0, 0.6);
  backdrop-filter: blur(4px);
  
  svg {
    color: rgba(255, 255, 255, 1);
    stroke-width: 2.5;
  }
  
  @media (max-width: 480px) {
    width: 28px;
    height: 28px;
    
    svg {
      width: 14px;
      height: 14px;
    }
  }
  
  @media (max-width: 360px) {
    width: 24px;
    height: 24px;
    
    svg {
      width: 12px;
      height: 12px;
    }
  }
`;


// The arrows alone don't say what a direction does. These name the two
// outcomes next to them, so a first-time player knows which way keeps.
export const InstructionLabel = styled.span`
  white-space: nowrap;
  opacity: 0.95;
  text-shadow: 0 1px 2px rgba(0, 0, 0, 0.4);
`;
